import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createShop } from '@/requests/shop'
import { uploadPresignedFile } from '@/requests/helper'
import { useAuthStore } from '@/stores/authStore'
import { useShop } from '@/context/shop-context'
import { useTranslation } from '@/hooks/use-translation'
import { toast } from '@/hooks/use-toast'
import { useShopCreationQuota } from '@/hooks/use-shop-creation-quota'
import { usePostCheckoutPolling } from '@/hooks/use-post-checkout-polling'
import { ShopCreationUpgradePanel, hasPendingShopCreate, clearPendingShopCreate } from './shop-creation-upgrade-panel'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Building2, Upload, Palette, Globe, Phone, Mail, MapPin } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import MultiLanguageInput from '@/components/multi-language-input'
import MultiLanguageEditor from '@/components/multi-language-editor'

const newShopSchema = z.object({
  name: z.record(z.string()).refine(
    (data) => Object.keys(data).length > 0 && Object.values(data).some(v => v.trim().length > 0),
    { message: 'Shop name is required in at least one language' }
  ),
  description: z.record(z.string()).refine(
    (data) => Object.keys(data).length > 0 && Object.values(data).some(v => v.trim().length > 0),
    { message: 'Shop description is required in at least one language' }
  ),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone is required'),
  address: z.string().min(1, 'Address is required'),
  currency: z.string().default('EUR'),
  primary_color: z.string().default('#ffb936'),
  secondary_color: z.string().default('#000000'),
  selected_header: z.string().default('header_1'),
  selected_body: z.string().default('body_1'),
  selected_menu: z.string().default('menu_1'),
  instagram_url: z.string().url().optional().or(z.literal('')),
})

type NewShopFormValues = z.infer<typeof newShopSchema>

interface NewShopDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewShopDrawer({ open, onOpenChange }: NewShopDrawerProps) {
  const { t } = useTranslation()
  const { accessToken } = useAuthStore((state) => state.auth)
  const user = useAuthStore.getState().auth.getUser()
  const { setActiveShop } = useShop()
  const queryClient = useQueryClient()
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [showUpgradeAfterError, setShowUpgradeAfterError] = useState(false)
  const { needsUpgrade, isPlatformAdmin, isLoading, refetch } = useShopCreationQuota()
  const { activated } = usePostCheckoutPolling()

  useEffect(() => {
    if (activated && hasPendingShopCreate()) {
      clearPendingShopCreate()
      void refetch()
    }
  }, [activated, refetch])

  const blockedByPlan = (needsUpgrade || showUpgradeAfterError) && !isPlatformAdmin

  const form = useForm<NewShopFormValues>({
    resolver: zodResolver(newShopSchema),
    defaultValues: {
      name: {},
      description: {},
      email: user?.email || '',
      phone: '',
      address: '',
      currency: 'EUR',
      primary_color: '#ffb936',
      secondary_color: '#000000',
      selected_header: 'header_1',
      selected_body: 'body_1',
      selected_menu: 'menu_1',
      instagram_url: '',
    },
  })

  const createShopMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await createShop(data, accessToken)

      // Upload logo if provided
      if (logoFile && response.logo_file?.pre_signed_url) {
        await uploadPresignedFile(logoFile, response.logo_file.pre_signed_url)
      }

      return response
    },
    onSuccess: (newShop) => {
      toast({
        title: 'Shop created successfully',
        description: 'Your new shop has been created and is ready to use.',
      })

      // Refresh shops list
      queryClient.invalidateQueries({ queryKey: ['shops.list'] })

      // Set as active shop
      setActiveShop(newShop)
      localStorage.setItem('lastSelectedShop', JSON.stringify(newShop))

      // Close drawer and reset form
      onOpenChange(false)
      form.reset()
      setLogoFile(null)
      setShowUpgradeAfterError(false)
    },
    onError: (error: any) => {
      const code = error.response?.data?.error?.code
      const message =
        error.response?.data?.error?.message ??
        error.response?.data?.message ??
        error.message ??
        'Failed to create shop. Please try again.'

      if (
        code === 'UNPROCESSABLE_ENTITY' &&
        typeof message === 'string' &&
        message.toLowerCase().includes('shop limit')
      ) {
        setShowUpgradeAfterError(true)
        void refetch()
      }

      toast({
        variant: 'destructive',
        title: 'Error creating shop',
        description: message,
      })
    },
  })

  const onSubmit = (data: NewShopFormValues) => {
    // Prepare social links
    const socialLinks = []
    if (data.instagram_url && data.instagram_url.trim()) {
      socialLinks.push({
        platform: 'instagram',
        url: data.instagram_url,
      })
    }

    const payload = {
      organization_id: user?.organization_id,
      name: data.name,
      status: 'ACTIVE',
      description: data.description,
      contact_information: {
        email: data.email,
        phone: data.phone,
        address: data.address,
        coordinates: '123,1231',
      },
      settings: {
        currency: data.currency,
        social_links: socialLinks,
        primary_color: data.primary_color,
        secondary_color: data.secondary_color,
        selected_menu: data.selected_menu,
        languages: ['tr', 'en'],
        selected_header: data.selected_header,
        selected_body: data.selected_body,
      },
      ...(logoFile && { logo_file: { name: logoFile.name } }),
    }

    createShopMutation.mutate(payload)
  }

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setLogoFile(file)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="new-shop-drawer">
        <SheetHeader className="space-y-3">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Building2 className="h-6 w-6" />
            {t('Create New Shop')}
          </SheetTitle>
          <SheetDescription className="text-base">
            {blockedByPlan
              ? t('Upgrade your account to create additional shops')
              : t('Fill in the details below to create a new shop for your organization.')}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-4 mt-8">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : blockedByPlan ? (
          <ShopCreationUpgradePanel onCancel={() => onOpenChange(false)} />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 mt-8">
            {/* Logo Upload Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Shop Logo</h3>
              </div>
              <div className="flex items-center gap-6 p-4 border rounded-lg bg-muted/30">
                <div className="h-20 w-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-background">
                  {logoFile ? (
                    <img
                      src={URL.createObjectURL(logoFile)}
                      alt="Logo preview"
                      className="h-full w-full object-cover rounded-xl"
                    />
                  ) : (
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <Button variant="outline" size="sm" asChild>
                    <label htmlFor="logo-upload" className="cursor-pointer">
                      <Upload className="h-4 w-4 mr-2" />
                      Choose File
                    </label>
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2">PNG, JPG up to 5MB</p>
                </div>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </div>
            </div>

            <Separator />

            {/* Basic Information */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Basic Information</h3>
              </div>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shop Name</FormLabel>
                    <FormControl>
                      <MultiLanguageInput
                        value={field.value || {}}
                        onChange={field.onChange}
                        requiredLangs={['tr', 'en']}
                        placeholder="Shop name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Shop Description</FormLabel>
                    <FormControl>
                      <MultiLanguageEditor
                        value={field.value || {}}
                        onChange={field.onChange}
                        requiredLangs={['tr', 'en']}
                        placeholder="Shop description"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Contact Information */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Contact Information</h3>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email Address
                      </FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="shop@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Phone Number
                        </FormLabel>
                        <FormControl>
                          <Input placeholder="+90 555 123 4567" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="currency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select currency" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="EUR">EUR (€)</SelectItem>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="TRY">TRY (₺)</SelectItem>
                            <SelectItem value="GBP">GBP (£)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Address
                      </FormLabel>
                      <FormControl>
                        <Textarea placeholder="Shop address" rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="instagram_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Instagram URL (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://instagram.com/yourshop" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Appearance Settings */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-semibold">Appearance Settings</h3>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="primary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Color</FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-3">
                          <Input type="color" className="w-16 h-10 p-1 rounded-md" {...field} />
                          <Input placeholder="#ffb936" {...field} className="flex-1" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="secondary_color"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secondary Color</FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-3">
                          <Input type="color" className="w-16 h-10 p-1 rounded-md" {...field} />
                          <Input placeholder="#000000" {...field} className="flex-1" />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="selected_header"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Header Style</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="header_1">Header 1</SelectItem>
                          <SelectItem value="header_2">Header 2</SelectItem>
                          <SelectItem value="header_3">Header 3</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="selected_body"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Body Style</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="body_1">Body 1</SelectItem>
                          <SelectItem value="body_2">Body 2</SelectItem>
                          <SelectItem value="body_3">Body 3</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="selected_menu"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Menu Style</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="menu_1">Menu 1</SelectItem>
                          <SelectItem value="menu_2">Menu 2</SelectItem>
                          <SelectItem value="menu_3">Menu 3</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createShopMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createShopMutation.isPending}>
                {createShopMutation.isPending ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </div>
                ) : (
                  'Create Shop'
                )}
              </Button>
            </div>
          </form>
        </Form>
        )}
      </SheetContent>
    </Sheet>
  )
}
